import { GlossaryItem } from "@/components/app/editor/glossary/GlossaryItem";
import { GlossaryItemEditDialog } from "@/components/app/editor/glossary/GlossaryItemEditDialog";
import { useEnable } from "@/hooks/useEnable";
import { editorClient } from "@/lib/editorClient";
import { Trans } from "@lingui/react/macro";
import { Button } from "@op/sf-editor";
import { usePublished } from "@op/utils";
import { FeatherPlus } from "@subframe/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/glossary/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { versionId, organizationSlug, projectSlug } = Route.useParams();

  const [editingGlossaryItem, setEditingGlossaryItem] = useState<
    string | undefined | null
  >(null);

  const queryClient = useQueryClient();

  const { data: glossaryItems } = useQuery(
    {
      queryKey: ["editor-version-glossary-items", { versionId }],
      queryFn: async () => {
        const res = await editorClient.versions[":versionId"][
          "glossary-items"
        ].$get({
          param: {
            versionId,
          },
        });
        if (!res.ok) {
          if (res.status === 401) throw redirect({ to: "/login" });
          throw new Error("Failed to get glossary items");
        }
        const data = await res.json();
        return data;
      },
    },
    queryClient
  );

  const currentGlossaryItem = useMemo(
    () =>
      glossaryItems?.find(
        (glossaryItem) => glossaryItem.id === editingGlossaryItem
      ),
    [glossaryItems, editingGlossaryItem]
  );

  useEffect(() => {
    if (editingGlossaryItem !== null) return;
    setTimeout(() => {
      document.body.style.pointerEvents = "auto";
    }, 1);
  }, [editingGlossaryItem]);

  const published = usePublished();

  const canCreate = useEnable(organizationSlug, projectSlug, [
    "admin",
    "publisher",
    "editor",
  ]);

  const createEnabled = useMemo(() => {
    return canCreate && !published;
  }, [canCreate, published]);

  return (
    <div className="w-full mx-auto flex flex-col gap-2 max-w-[1280px] px-4">
      <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background">
        <div className="flex w-full items-center justify-end gap-4">
          <Button
            variant="brand-secondary"
            size="small"
            icon={<FeatherPlus />}
            onClick={() => setEditingGlossaryItem(undefined)}
            disabled={!createEnabled}
          >
            <Trans>Add Glossary Item</Trans>
          </Button>
        </div>
        {glossaryItems?.map((glossaryItem) => (
          <GlossaryItem
            key={glossaryItem.id}
            id={glossaryItem.id}
            versionId={versionId}
            organizationSlug={organizationSlug}
            projectSlug={projectSlug}
            term={glossaryItem.term}
            definition={glossaryItem.definition}
            onEdit={setEditingGlossaryItem}
          />
        ))}
      </div>
      <GlossaryItemEditDialog
        versionId={versionId}
        glossaryItemId={editingGlossaryItem}
        term={currentGlossaryItem?.term}
        definition={currentGlossaryItem?.definition}
        organizationSlug={organizationSlug}
        projectSlug={projectSlug}
        onOpenChange={setEditingGlossaryItem}
      />
    </div>
  );
}
